<template>
  <PageContent>
    <div class="flex flex-col gap-4">
      <NuxtLink
        :to="localePath(back)"
        class="text-md flex items-center self-start text-gray-600"
      >
        <LazyIconFielmannArrowLeft class="mb-[0.1em] mr-2 size-5" />
        {{ $t('store_finder.details_page.go_back') }}
      </NuxtLink>

      <Headline tag="h1" look="h2"> {{ title }} </Headline>
      <StoreStatus :opening-details="store" />
      <div
        class="grid grid-flow-row-dense grid-cols-1 gap-4 md:grid-cols-2"
        :class="{ 'grid-rows-auto': !contentBlocks.length }"
      >
        <BoxedContent
          v-if="contentBlocks.length"
          class="flex flex-col gap-4 bg-gray-200"
        >
          <div>
            <component
              :is="blok.component"
              v-for="blok in contentBlocks"
              :key="blok._uid"
              :preload-media="true"
              :blok="blok"
              class="mx-auto max-w-7xl"
            />
          </div>
        </BoxedContent>

        <div
          v-if="isRopoSupported"
          class="flex flex-col bg-gradient-to-r from-[#EBEAE6] to-[#F7F5F180] p-4 md:block md:p-7"
        >
          <span class="text-lg font-medium">{{ $t('ropo.ndl.overline') }}</span>
          <Headline
            tag="h3"
            class="my-3 text-3xl font-medium md:my-4 md:text-4xl"
          >
            {{ $t('ropo.ndl.headline') }}
          </Headline>
          <div
            v-dompurify-html="$t('ropo.ndl.text')"
            class="order-1 mb-4 mt-6 md:order-none md:mt-4"
          ></div>
          <FimButton
            no-padding
            type="primary"
            class="mt-1 w-full rounded-lg px-6 py-3 text-lg md:w-auto"
            @click="gotoRopoBasket(store)"
            >{{ $t('ropo.ndl.cta') }}</FimButton
          >
        </div>

        <BoxedContent class="flex flex-col gap-3 overflow-hidden leading-7">
          <Headline tag="h3" look="h3"> {{ appointmentHeadline }} </Headline>
          <div class="flex flex-col gap-3">
            <div>
              {{ appointmentText }}
            </div>
            <StoreTermin :store="store" horizontal />
          </div>
        </BoxedContent>
        <div
          class="order-4 md:order-none"
          :class="{
            'md:row-span-full': !contentBlocks.length && !isRopoSupported,
          }"
        >
          <BoxedContent class="flex flex-col gap-3">
            <Headline tag="h2" look="h3">
              {{
                descriptionHeadline ||
                $t('store_finder.details_page.content.title', {
                  serviceOffering: isHearingAid
                    ? $t(
                        'store_finder.details_page.service_offering.optician_hearing_acoustics',
                      )
                    : $t(`store_finder.details_page.service_offering.optician`),
                  city: store.city,
                })
              }}
            </Headline>
            <div v-if="descriptionText">{{ descriptionText }}</div>
            <template v-else>
              <p>
                {{
                  $t('store_finder.details_page.content.text.paragraph_1', {
                    city: store.city,
                    street: store.street,
                    name: getStoreName(store).split(',')[0],
                    firmierung: store.firmierung,
                  })
                }}
              </p>
              <p>
                {{ $t('store_finder.details_page.content.text.paragraph_2') }}
              </p>
              <p>
                {{ $t('store_finder.details_page.content.text.paragraph_3') }}
              </p>
              <I18nT
                v-if="isEyeCheckUp"
                keypath="store_finder.details_page.content.text.paragraph_eyecheckup"
                tag="p"
              >
                <template #link>
                  <FimLink
                    :to="eyeCheckUpLink"
                    :only-exact-active="true"
                    target="_blank"
                    type="underline"
                    text-size="inherit"
                  >
                    {{ $t('store_finder.search.options.AC') }}
                  </FimLink>
                </template>
              </I18nT>
              <p v-if="isHearingAid">
                {{
                  $t(
                    'store_finder.details_page.content.text.paragraph_hearingaid',
                    {
                      city: store.city,
                    },
                  )
                }}
              </p>
              <p>
                {{
                  $t('store_finder.details_page.content.text.paragraph_last')
                }}
              </p>
            </template>
          </BoxedContent>
        </div>
        <BoxedContent class="order-3 leading-7 md:order-none">
          <div class="flex flex-col gap-5">
            <Headline tag="h3" look="h4">
              {{ $t('store_finder.details_page.content.address') }}
            </Headline>
            <div class="flex flex-col gap-2">
              <Headline tag="h4" look="h5"> Fielmann </Headline>
              <div>
                {{ store.street }}<br />
                <template v-if="commonName">{{ commonName }}<br /></template>
                {{ `${store.zip} ${store.city}` }}<br />
                <div v-if="store.phone">
                  {{ $t('store_finder.details_page.content.phone') }}:
                  {{ store.phone }}<br />
                </div>
                <div v-if="store.fax">
                  {{ $t('store_finder.details_page.content.fax') }}:
                  {{ store.fax }}<br />
                </div>
              </div>
            </div>
            <div v-if="openingHours" class="flex flex-col gap-2 tabular-nums">
              <Headline tag="h4" look="h5">
                {{ $t('store_finder.details_page.content.opening_hours') }}
              </Headline>
              <dl
                v-if="Array.isArray(openingHours) && openingHours.length"
                class="grid grid-cols-[min-content_1fr]"
              >
                <template
                  v-for="(row, index) in openingHours"
                  :key="`opening-day-${index}`"
                >
                  <dt class="mr-2 whitespace-nowrap">
                    {{ row.day }}
                  </dt>
                  <!-- eslint-disable-next-line vue/require-v-for-key -->
                  <dd class="flex flex-col">
                    <span
                      v-for="hour in row.status"
                      :key="`opening-hour-${index}-${hour}`"
                      >{{ hour }}</span
                    >
                  </dd>
                </template>
              </dl>
            </div>
            <div
              v-if="nextSpecialOpeningHours.length"
              class="flex flex-col gap-2 tabular-nums"
            >
              <Headline tag="h4" look="h5">
                {{
                  $t('store_finder.details_page.content.special_opening_hours')
                }}
              </Headline>
              <dl class="grid grid-cols-[min-content_1fr]">
                <template v-for="(day, index) in nextSpecialOpeningHours">
                  <dt
                    v-if="day && day.date"
                    :key="`special-opening-day-${index}`"
                    class="mr-3 whitespace-nowrap"
                  >
                    {{ day.dateString }}
                  </dt>
                  <!-- eslint-disable-next-line vue/require-v-for-key -->
                  <dd class="flex flex-col">
                    <span
                      v-for="hour in day.status"
                      :key="`spec-opening-hour-${index}-${hour}`"
                      >{{ hour }}</span
                    >
                  </dd>
                </template>
              </dl>
            </div>
          </div>
        </BoxedContent>
      </div>
      <div>
        <Headline tag="h3" look="h4" class="mb-5 mt-4">
          {{ $t('store_finder.details_page.content.more_stores') }}
        </Headline>
        <div
          class="grid w-full grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
        >
          <BoxedContent
            v-for="additionalStore in store.additionalStores"
            :key="additionalStore.id"
            class="w-full leading-7"
            tight-spacing
          >
            <Headline look="h5" tag="h4">
              {{ getStoreHeadline(additionalStore, $i18n) }}
            </Headline>
            <div class="flex flex-col">
              <div>{{ additionalStore.street }}</div>
              <div>
                {{ `${additionalStore.zip} ${additionalStore.city}` }}
              </div>
              <div class="flex">
                <div class="flex flex-col justify-center">
                  <LazyIconFielmannPin class="mr-1 size-4" />
                </div>
                <div class="flex flex-col justify-center">
                  {{ additionalStore.distance.toFixed(2) }} km
                </div>
              </div>
            </div>
            <StoreStatus :opening-details="additionalStore" />
            <div class="pt-3">
              <FimButton
                :id="`store-${additionalStore.id}`"
                type="secondary"
                :to="detailsUrl(additionalStore)"
                @mousedown="trackClick"
              >
                <template #icon>
                  <LazyIconFielmannStore class="size-6" />
                </template>
                {{ $t('store_finder.details_page.content.more_info') }}
              </FimButton>
              <FimButton
                v-if="isStoreRopoSupported(additionalStore)"
                class="pl-0"
                type="link-alternative"
                @click="gotoRopoBasket(additionalStore)"
              >
                <template #iconAfter>
                  <LazyIconFielmannArrowRight class="h-5 w-4" />
                </template>
                {{ $t('ropo.ndl.list_cta') }}
              </FimButton>
            </div>
          </BoxedContent>
        </div>
      </div>
    </div>
  </PageContent>
</template>
<script setup lang="ts">
import type { OpeningHoursSpecification } from 'schema-dts'
import Headline from '~/components/ui/headlines/Headline.vue'
import FimButton from '~/components/ui/buttons/FimButton.vue'
import PageContent from '~/components/layout/PageContent.vue'
import StoreStatus from '~/components/store-finder/StoreStatus.vue'
import StoreTermin from '~/components/store-finder/StoreTermin.vue'
import BoxedContent from '~/components/store-finder/BoxedContent.vue'
import type {
  StoreHours,
  IStore,
  IWeekdayKey,
  ISpecialOpening,
} from '~/rpcMethods/stores'
import { useFeatureToggles } from '@/composables/useFeatureToggles'
import { useRopoBasketState } from '~/composables/useRopoBasketState'
import type { StoryblokStory } from 'storyblok-generate-ts'
import type { SbPage } from '~/storyblok/types/storyblok.gen'

const localePath = useFimLocalePath()

const props = defineProps({
  store: {
    type: Object as PropType<IStore>,
    required: true,
  },
  globals: {
    type: Object as PropType<StoryblokStory<SbPage> | null>,
    default: () => {},
  },
})

const { $currentShop, $i18n } = useNuxtApp()
const route = useRoute()
const { trackButtonClick } = await await useTrackingEvents()

const trackClick = (event: Event) => {
  const element = event?.target as HTMLButtonElement
  trackButtonClick(element)
}

const back = computed(() => {
  const previousPage = route.redirectedFrom?.fullPath
  return previousPage ?? { name: routeList.stores.name }
})

const detailsUrl = (store: IStore) => {
  return getStoreUrl(store, localePath)
}

const specialOpeningHours = computed((): StoreHours[] => {
  const hours = props?.store?.special_opening_hours
    ? (JSON.parse(props?.store?.special_opening_hours) as ISpecialOpening[])
    : []
  return (
    hours
      .map((special) => formatSpecialOpeningHours(special, $currentShop, $i18n))
      .filter(
        (special) =>
          compareDateByDay(
            special.date,
            (route?.query?.date as string) ?? new Date(),
          ) <= 0,
      ) ?? []
  )
})

// Until API is aligned show only a few next special opening hours
const nextSpecialOpeningHours = computed(() =>
  specialOpeningHours.value.slice(0, 10),
)

const openingHours = computed((): StoreHours[] => {
  const daysTable = {
    '010': 'mon',
    '020': 'tue',
    '030': 'wed',
    '040': 'thu',
    '050': 'fri',
    '060': 'sat',
    '070': 'sun',
  }

  const hours = props.store.structured_opening_hours ?? []
  return Object.entries(daysTable).flatMap(([key, day]) => {
    const dayString = $i18n.t(`store_finder.details_page.content.${day}`)
    const date = getDateFromKey(
      key as IWeekdayKey,
      (route?.query?.date as string) ?? Date.now(),
    )
    const regular = hours.find((data) => data.key[1] === key[1])

    return [
      {
        date,
        day: dayString,
        status: regular
          ? formatRegularOpeningHours(regular.value)
          : [$i18n.t('store_finder.details_page.content.closed')],
      },
    ]
  })
})

const appointmentType = computed(() => {
  return props.store.appointment_type ?? []
})

const title = computed(() => getStoreHeadline(props.store, $i18n, true))

const { ropoConfig } = await useFeatureToggles()
const { gotoRopoBasket } = await useRopoBasketState()

const isStoreRopoSupported = (store: IStore) => {
  return (
    ropoConfig?.value?.enabled &&
    ropoConfig?.value?.supportedStores.includes(store.recordsetID)
  )
}

const isRopoSupported = computed(() => isStoreRopoSupported(props.store))

const isHearingAid = computed(() =>
  props.store?.appointment_type?.includes('HA'),
)
const isEyeCheckUp = computed(() =>
  props.store?.appointment_type?.includes('AC'),
)
const eyeCheckUpLink = computed(() => {
  // @todo @hack there should be a better way, to get the localized link for a storyblok page...
  return (
    $i18n.t(
      'store_finder.details_page.content.text.paragraph_eyecheckup_link_path',
    ) || '/augen-check-up'
  )
})

const contentBlocks = computed(() => props.globals?.content?.content ?? [])
const descriptionHeadline = computed(() =>
  getStoreDescriptionHeadline(props.store),
)
const descriptionText = computed(() => getStoreDescriptionText(props.store))
const commonName = computed(() => getStoreCommonName(props.store))
const appointmentHeadline = computed(
  () =>
    getStoreAppointmentHeadline(props.store) ||
    $i18n.t('store_finder.details_page.content.book_title'),
)
const appointmentText = computed(
  () =>
    getStoreAppointmentText(props.store) ||
    $i18n.t('store_finder.details_page.content.book_subtitle'),
)

useJsonld({
  '@context': 'https://schema.org',
  '@type': 'Optician',
  '@id': getUrl($currentShop.domain, route.fullPath),
  name: title?.value,
  alternateName: 'Brille: Fielmann',
  description: $i18n.t('store_finder.details_page.content.text.paragraph_1', {
    city: props.store.city,
    street: props.store.street,
    name: getStoreName(props.store).split(',')[0],
    firmierung: props.store.firmierung,
  }),
  geo: {
    '@type': 'GeoCoordinates',
    latitude: props.store.storeLocation.coordinates[0],
    longitude: props.store.storeLocation.coordinates[1],
  },
  address: {
    '@type': 'PostalAddress',
    streetAddress: props.store.street,
    addressLocality: props.store.city,
    addressRegion: props.store.region,
    postalCode: props.store.zip,
    addressCountry: props.store.area,
  },
  image: $currentShop.domain + '/img/fielmann-logo_black.svg',
  url: getUrl($currentShop.domain, route.fullPath),
  openingHoursSpecification:
    openingHours.value?.flatMap((data) => {
      const firstHours = data.status.at(0)
      const lastHours = data.status.at(-1)
      const opens = firstHours && firstHours.split('-')[0]
      const closes = lastHours && lastHours.split('-')[1]

      if (opens && closes) {
        return [
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: [
              toLocaleDateTimeString(
                data.date,
                { locale: 'en' },
                {
                  weekday: 'long',
                },
              ),
            ],
            opens: opens.replace('.', ':'),
            closes: closes.replace('.', ':'),
          } as OpeningHoursSpecification,
        ]
      }

      return []
    }) ?? [],
  priceRange: '€-€€€',
  parentOrganization: {
    '@type': 'Corporation',
    name: 'Fielmann AG',
    url: 'https://www.fielmann.de',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: props.store.phone,
    contactType: 'Kundenservice',
    areaServed: 'DE',
  },
  department: {
    '@type': 'MedicalBusiness',
    name: title?.value,
    makesOffer: appointmentType.value.map((i) => {
      return {
        '@type': 'Offer',
        name: $i18n.t(`store_finder.search.options.${i}`),
        description: $i18n.t(`store_finder.search.options.${i}`) + ' Service',
      }
    }),
  },
})

defineOptions({ name: 'StoreDetails' })
</script>
<style>
.grid-rows-auto {
  @media (width >= 768px) {
    grid-template-rows: auto auto;
  }
}
</style>
